import { Link } from 'react-router'
import { ArrowUpRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { TooltipProvider } from '@/components/ui/tooltip'
import { FlagChips, PlanBadge, StateBadge, SyncStatus } from '@/components/data/badges'
import { UsageBar } from '@/components/data/meters'
import { MonoId } from '@/components/data/mono-id'
import { formatDate, formatMoney, formatRelative } from '@/lib/format'
import type { TeamSummary } from '@/lib/types'

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right">{children}</dd>
    </div>
  )
}

/** A quick look at a team without leaving the list. */
export function TeamDrawer({ team, onClose }: { team: TeamSummary | undefined; onClose: () => void }) {
  return (
    <Sheet open={!!team} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="gap-0 sm:max-w-md">
        {team && (
          <TooltipProvider>
            <SheetHeader className="border-b pr-12">
              <SheetTitle className="text-lg">{team.name || `Team ${team.id}`}</SheetTitle>
              <SheetDescription asChild>
                <div className="flex flex-wrap items-center gap-2">
                  <PlanBadge planId={team.plan_id} name={team.plan_name} />
                  <StateBadge state={team.subscription.state} />
                </div>
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {team.flags.length > 0 && (
                <div className="border-b py-3">
                  <FlagChips flags={team.flags} max={6} />
                </div>
              )}
              <dl className="divide-y">
                <Row label="Owner">{team.owner?.email || team.owner?.uid || '—'}</Row>
                <Row label="Members">
                  <UsageBar value={team.members} limit={team.limits.users} className="ml-auto w-36" />
                </Row>
                <Row label="Companies">
                  <UsageBar value={team.companies} limit={team.limits.companies} className="ml-auto w-36" />
                </Row>
                <Row label="History rows">
                  <span className="figures">{team.snapshots.toLocaleString('en-US')}</span>
                </Row>
                <Row label="HubSpot">{team.hubspot ? <SyncStatus status={team.hubspot.status} /> : 'Not connected'}</Row>
                <Row label="Zapier">{team.zapier ? 'Connected' : 'Not connected'}</Row>
                <Row label="Last active">{formatRelative(team.last_active_at)}</Row>
                {team.subscription.state !== 'free' && (
                  <>
                    <Row label={team.subscription.state === 'canceling' ? 'Ends' : 'Renews'}>{formatDate(team.subscription.expires_at)}</Row>
                    <Row label="Value per month">{formatMoney(team.subscription.monthly_cents)}</Row>
                    <Row label="Stripe customer">
                      <MonoId value={team.subscription.stripe_customer_id} />
                    </Row>
                  </>
                )}
                <Row label="Team id">
                  <MonoId value={String(team.id)} />
                </Row>
              </dl>
            </div>

            <div className="border-t p-4">
              <Button asChild className="w-full">
                <Link to={`/teams/${team.id}`}>
                  Open team <ArrowUpRight />
                </Link>
              </Button>
            </div>
          </TooltipProvider>
        )}
      </SheetContent>
    </Sheet>
  )
}
